import { isFeatureEnabled } from 'owa-feature-flags';
import type { HostAppProvidedPersonaIdentifiers } from 'owa-people-loki/lib/models/models';
import type { Dispatch } from 'redux';
import {
    HubPersona,
    Person,
    createActionFactory,
    createActionFactoryForFeature,
    favoritesLeftNavItemId,
    Feature,
    createThunk,
    PersonType,
    isContactsOrContactListsLeftNavItem,
    ThunkDispatch,
    sequentiallyProcessPromiseFactories,
    UnifiedGroupBasicData,
    UnifiedGroup,
    contactsLeftNavItemId,
} from '../../../shared';
import {
    getAppState,
    getActiveLeftNavItemId,
    isActiveLeftNavItemDeletedItems,
    isInSearchMode,
    Filter,
    Sorting,
    debouncedSerializeAndPersistPeopleHubOptions,
    getPackageState,
    packageStateKey,
    getActiveLeftNavItemSparseFolder,
} from '../../../appstate';
import { getPersona, getPersonasInFolder, loadDeletedItemsFolder } from '../data/personaActions';
import { getGroupDetailsData } from '../data/groups/unifiedGroupActions';
import { searchRefresh } from '../../../searchbox';
import { getPublicFolderForCurrentHubPersona } from '../../selectors/publicFolderSelector';
import { getCurrentHubPersonas } from '../../selectors/currentPersonaSelector';
import type { HubState } from '../../state';
import type DisplayOrder from 'owa-people-types/lib/types/DisplayOrder';
import { getPersonasInSparseFolder } from '../data/sparseFolderActions';

export type { HubPersona };

const actionFactory = createActionFactoryForFeature(Feature.PersonaList);

export const personaListFilterChangedAction = createActionFactory<{ filter: Filter }>(
    'PersonaListFilterChangedAction',
    Feature.PersonaList,
    action => ({
        filter: action.payload.filter,
    })
);

export const personaListSortingChangedAction = actionFactory<{
    sorting: Sorting;
    isSearchMode: boolean;
    activeLeftNavItemId: string;
}>('PersonaListSortingChangedAction');

export const personaListDisplayOrderChangedAction = actionFactory<{ displayOrder: DisplayOrder }>(
    'PersonaListDisplayOrderChangedAction'
);

export const personaListScrollToGroupClickedAction = actionFactory<{
    firstHubPersonaIdInGroup: string;
}>('personaListScrollToGroupClickedAction');

export const checkedPersonasClearedAction = actionFactory<{}>('CheckedPersonasClearedAction');

export const allPersonasCheckedAction = actionFactory<{ hubPersonaIds: string[] }>(
    'CheckedPersonasChangedAction'
);

export const checkPersonaListItemToggledAction = actionFactory<{
    checkedHubPersonaIds: string[];
    uncheckedHubPersonaIds: string[];
}>('CheckPersonaListItemToggledAction');

export const mouseOverHubPersonaAction = actionFactory<{
    hubPersonaId: string;
}>('MouseOverHubPersonaAction');

export const mouseLeaveHubPersonaAction = actionFactory<{}>('MouseLeaveHubPersonaAction');

export const personaListRetryAction = actionFactory<{}>('PersonaListRetryAction');

export const personaClickedAction = actionFactory<{
    hubPersonaId: string;
    origin: PersonaClickedOrigin;
}>('PersonaClickedAction');

export const personaFocusAction = actionFactory<{
    hubPersonaId: string;
}>('PersonaFocusAction');

export const personaShiftClickedAction = actionFactory<{
    hubPersonaId: string;
    origin: PersonaClickedOrigin;
}>('PersonaShiftClickedAction');

export type PersonaClickedOrigin =
    | 'personaList'
    | 'personaListSearch'
    | 'PersonaExpandedView'
    | 'PrivateDistributionListView';

export const personaListViewFilterChanged = (dispatch: Dispatch) => (filter: Filter) => {
    dispatch(personaListFilterChangedAction({ filter }));
};

export const personaListFetchSparseItem = createThunk(
    'PersonaListFetchSparseItem',
    (dispatch: ThunkDispatch, getState: () => {}) => (itemId: number) => {
        const appState = getAppState(getState());
        const sparseFolder = getActiveLeftNavItemSparseFolder(appState);
        if (sparseFolder) {
            dispatch(getPersonasInSparseFolder)(sparseFolder.id, itemId);
        }
    }
);

export const personaListVisibleEntriesChanged = createThunk(
    'PersonaListVisibleEntriesChanged',
    (dispatch: ThunkDispatch, getState: () => {}) => (entryIds: string[]) => {
        const appState = getAppState(getState());
        const personas = entryIds
            .map(entryId => {
                const persona =
                    appState.data.personas[entryId] ||
                    appState.data.groupsHubState.unifiedGroupsMap[entryId];
                return persona;
            })
            .filter(persona => !!persona);
        prefetchRequestedPersonas(personas);
        const unresolvedGroups = personas.filter(
            persona => persona.type === PersonType.GroupBasicData
        ) as UnifiedGroupBasicData[];
        sequentiallyProcessPromiseFactories(
            unresolvedGroups.map(unresolvedGroup => () =>
                getGroupDetailsData(dispatch)(
                    unresolvedGroup.externalDirectoryObjectId,
                    unresolvedGroup.emailAddress,
                    unresolvedGroup.hubPersonaId
                ).catch(error => void 0)
            )
        );
    }
);

export const personaListViewSortingChanged = createThunk(
    'PersonaListSortingChangeClicked',
    (dispatch: ThunkDispatch, getState: () => {}) => (sorting: Sorting) => {
        const appState = getAppState(getState());
        const isSearchMode = isInSearchMode(appState);
        const activeLeftNavItemId = getActiveLeftNavItemId(appState);
        dispatch(personaListSortingChangedAction({ sorting, isSearchMode, activeLeftNavItemId }));
        debouncedSerializeAndPersistPeopleHubOptions(dispatch);
    }
);

export const personaListViewDisplayOrderChanged = (dispatch: Dispatch) => (
    displayOrder: DisplayOrder
) => {
    dispatch(personaListDisplayOrderChangedAction({ displayOrder }));
};

export const mouseOverHubPersona = (dispatch: Dispatch) => (hubPersonaId: string) => {
    if (isFeatureEnabled('people-hublist-itemMenu')) {
        dispatch(mouseOverHubPersonaAction({ hubPersonaId: hubPersonaId }));
    }
};

export const mouseLeaveHubPersona = (dispatch: Dispatch) => () => {
    if (isFeatureEnabled('people-hublist-itemMenu')) {
        dispatch(mouseLeaveHubPersonaAction());
    }
};

export const allPersonasChecked = (dispatch: Dispatch) => (hubPersonaIds: string[]) =>
    dispatch(allPersonasCheckedAction({ hubPersonaIds }));

export const checkedPersonasCleared = (dispatch: Dispatch) => () =>
    dispatch(checkedPersonasClearedAction());

export const checkPersonaListItemToggled = createThunk(
    'CheckPersonaListItemToggled',
    (dispatch: ThunkDispatch, getState: () => {}) => (
        checkedPersonas: HubPersona[],
        uncheckedPersonas: HubPersona[],
        fetchMoreDetails: boolean
    ) => {
        dispatch(
            checkPersonaListItemToggledAction({
                checkedHubPersonaIds: checkedPersonas.map(persona => persona.hubPersonaId),
                uncheckedHubPersonaIds: uncheckedPersonas.map(persona => persona.hubPersonaId),
            })
        );

        const state = getPackageState<HubState>(getState(), packageStateKey);
        const maxContactsToFetch =
            state.appState.data.userConfiguration.userOptions.MaximumNumberOfContactsPerPerson;
        const currentHubPersonas = getCurrentHubPersonas(state.appState, state.packageState);

        const activeLeftNavItemId = getActiveLeftNavItemId(state.appState);
        const isCAPIv3Enabled = state.appState.view.layoutState.isCAPIv3Enabled;

        if (
            (isCAPIv3Enabled && activeLeftNavItemId === contactsLeftNavItemId) ||
            !fetchMoreDetails ||
            currentHubPersonas.length > maxContactsToFetch
        ) {
            return;
        }

        // When deselecting personas, getPersona might need to get called for some of the currentHubPersonas
        const personasToFetch = uncheckedPersonas.length ? currentHubPersonas : checkedPersonas;
        const publicFolder = getPublicFolderForCurrentHubPersona(
            state.appState,
            state.packageState
        );
        const personaListCheckedHubPersonaDetails =
            state.appState.view.navigationState.personaListCheckedHubPersonaDetails;

        // In order to limit firing large number of getPersona calls, since Link API does not
        // support linking of more than 50 contacts, a check is added to break from the loop
        let contactsFetchedCount = 0;
        for (let persona of personasToFetch) {
            // Do getPersona to get Linked Contacts which is necessary for "link contacts" button in multi select view
            if (
                persona.type === PersonType.Person ||
                (persona.type === PersonType.FavoritePerson && persona.personaId)
            ) {
                // Check if getPersona has already been called
                if (
                    !uncheckedPersonas.length ||
                    !personaListCheckedHubPersonaDetails.find(
                        checkedHubPersonaDetails =>
                            checkedHubPersonaDetails.hubPersonaId === persona.hubPersonaId
                    ).dataFromGetPersona
                ) {
                    getPersona(dispatch)(persona.personaId, publicFolder ? publicFolder.id : null);
                }
                contactsFetchedCount +=
                    persona.type === PersonType.Person ? persona.totalNumberOfLinkedContacts : 0;
                if (contactsFetchedCount > maxContactsToFetch) {
                    break;
                }
            }
        }
    }
);

export const personaListItemClicked = (dispatch: Dispatch) => (
    persona: HubPersona,
    origin: PersonaClickedOrigin
) => {
    dispatch(personaClickedAction({ hubPersonaId: persona.hubPersonaId, origin }));
};

export const personaListItemFocus = (dispatch: Dispatch) => (persona: HubPersona) => {
    dispatch(personaFocusAction({ hubPersonaId: persona.hubPersonaId }));
};

export const personaListShiftItemClicked = (dispatch: Dispatch) => (
    persona: HubPersona,
    origin: PersonaClickedOrigin
) => dispatch(personaShiftClickedAction({ hubPersonaId: persona.hubPersonaId, origin }));

export const personaListScrollToGroupClicked = (dispatch: Dispatch) => (
    firstPersonaHubIdInGroup: string
) =>
    dispatch(
        personaListScrollToGroupClickedAction({
            firstHubPersonaIdInGroup: firstPersonaHubIdInGroup,
        })
    );

export const refreshCurrentPersonaListData = createThunk(
    'RefreshCurrentPersonaListData',
    (dispatch: ThunkDispatch, getState: () => {}): void => {
        const appState = getAppState(getState());

        if (isInSearchMode(getAppState(getState()))) {
            dispatch(searchRefresh);
            return;
        }

        const activeLeftNavItemId = getActiveLeftNavItemId(appState);

        if (activeLeftNavItemId === favoritesLeftNavItemId) {
            return;
        } else if (isActiveLeftNavItemDeletedItems(appState)) {
            loadDeletedItemsFolder(dispatch);
        } else if (isContactsOrContactListsLeftNavItem(activeLeftNavItemId)) {
            const rootFolder = appState.data.contactFoldersState.rootContactFolder;
            getPersonasInFolder(dispatch)(rootFolder.id);
        } else {
            const activeLeftNavItemIsSparseFolder = !!getActiveLeftNavItemSparseFolder(appState);
            if (!activeLeftNavItemIsSparseFolder) {
                getPersonasInFolder(dispatch)(activeLeftNavItemId);
            }
        }
    }
);

export const personaListRetryActionClicked = createThunk(
    'PersonaListRetryActionClicked',
    (dispatch: ThunkDispatch, getState: () => {}): void => {
        dispatch(personaListRetryAction());
        refreshCurrentPersonaListData(dispatch, getState);
    }
);

function makeHostAppIdentifierFromGroup(
    group: UnifiedGroupBasicData | UnifiedGroup
): HostAppProvidedPersonaIdentifiers {
    return {
        Smtp: group.emailAddress,
        AadObjectId: group.externalDirectoryObjectId,
        PersonaType: 'Group',
        OlsPersonaId: '',
    };
}

function makeHostAppIdentifierFromPersona(persona: Person): HostAppProvidedPersonaIdentifiers {
    return {
        Smtp: persona.info.mainEmailAddress,
        AadObjectId: persona.info.GALContactGuid,
        PersonaType: 'User',
        OlsPersonaId: persona.personaId,
    };
}

const alreadyPrefetchedPersonas: { [idOrEmail: string]: boolean } = {};
export function prefetchRequestedPersonas(personas: (HubPersona | UnifiedGroup)[]): void {
    const lpc = window['LPC'];
    if (!lpc) {
        return;
    }
    const hostAppProvidedPersonaIdentifiers = personas
        .map(persona => {
            if (
                persona &&
                (persona.type === PersonType.GroupBasicData || persona.type === PersonType.Group)
            ) {
                return makeHostAppIdentifierFromGroup(persona);
            }

            if (persona && persona.type === PersonType.Person) {
                return makeHostAppIdentifierFromPersona(persona);
            }

            return undefined;
        })
        .filter(
            identifier =>
                !!identifier &&
                !alreadyPrefetchedPersonas[identifier.OlsPersonaId || identifier.Smtp]
        );

    hostAppProvidedPersonaIdentifiers.forEach(
        identifier => (alreadyPrefetchedPersonas[identifier.OlsPersonaId || identifier.Smtp] = true)
    );

    if (hostAppProvidedPersonaIdentifiers.length > 0) {
        lpc.prefetchPersonas(hostAppProvidedPersonaIdentifiers, 'Expanded');
    }
}
export type { Action } from '../../../shared';
